<template>
  <div class="profile-sharing-editor">
    <div v-if="isResearchRef">
      <label class="form-label">This profile belongs to this tree</label>
      <ResearchGroupItem :data-group-id="dataGroupRef?.id" ensure-loaded/>
    </div>
    <template v-if="profileTypeRef == ProfileType.Research">
      <div v-if="personRef?.restricted" class="alert alert-primary alert-sm">
        This profile is restricted. It is only visible to the tree owner.
      </div>
      <div v-else class="help-text">
        This profile is visible to anyone the tree is shared with.
      </div>
    </template>
    <div v-else-if="!isSharedOrRequestedRef" class="alert alert-primary alert-sm">
      This profile is not shared with any groups.
    </div>
    <template v-else>
      <div class="help-text">
        Sharing a profile with a group allows members of the group to see the profile in their family tree.
      </div>
      <ul class="list-unstyled shared-list">
        <li v-for="inv in dataGroupInvitesRef" :key="inv.id">
          <FamilyGroupItem 
            :family-group-id="inv.principalId"
            select-mode
            :status="PendingStatus"
            @click="onSelectDataGroupInvite(inv)"/>
        </li>
        <li v-for="dm in familyGroupMembersRef" :key="dm.id">
          <FamilyGroupItem :family-group-id="dm.principalId"/>
          <div class="item-actions">
            <button type="button" v-if="canEditRef" class="btn btn-inline btn-link" @click="onRemoveFamilyGroup(dm)">
              Remove
            </button>
          </div>
        </li>
      </ul>
    </template>
    <div class="actions">
      <template v-if="canEditSharingRef">
        <button type="button" v-if="isSharedOrRequestedRef" class="btn btn-inline btn-link" @click="onShare">
          Share with another group
        </button>
        <button type="button" v-else class="btn btn-primary" @click="onShare">
          <PlusIcon size="1.2x" class="mb-1"/> Share this profile
        </button>
      </template>
    </div>
    <DataGroupShareModal ref="dataGroupShareModalRef" profile @request-share="onRequestShare"/>
    <DataGroupInviteModal ref="dataGroupInviteModalRef"/>
    <DataGroupMemberRemoveModal ref="removeFamilyGroupModalRef" profile/>
  </div>
</template>

<style lang="scss" scoped>
.profile-sharing-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

h5 {
  font-size: 1rem;
  font-weight: normal;
}

.shared-list {
  margin-left: 1rem;

  li {
    margin-bottom: 0.5rem;
  }
}

.item-actions {
  margin-top: 4px;
  margin-left: calc(35px + 0.5rem); // HACK: img width from CompactItem
  font-size: 0.875rem;
}

.actions .btn-link {
  font-size: 0.875rem;
}

</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useDataGroupInviteStore } from '@/gp/DataGroupInviteStore'
import { useUserStore } from '@/gp/UserStore'
import { usePersonStore } from '@/rd/PersonStore'
import { LoadMode } from '@/util/AsyncData'
import { ProfileUtils, ProfileType } from './ProfileUtils'
import { CompositeId } from '@/rd/CompositeId'
import { DataGroupInvite, DataGroupMember, DataGroupType, InvitationResult, ItemPermissions, PrincipalType } from '@/gp/GroupAdminModel'
import { PlusIcon } from '@zhuowenli/vue-feather-icons'
import FamilyGroupItem from '@/manage/familygroups/FamilyGroupItem.vue'
import ResearchGroupItem from '@/manage/researchgroups/ResearchGroupItem.vue'
import DataGroupShareModal from '@/manage/datagroups/DataGroupShareModal.vue'
import DataGroupInviteModal from '@/manage/datagroups/DataGroupInviteModal.vue'
import DataGroupMemberRemoveModal from '@/manage/datagroups/DataGroupMemberRemoveModal.vue'
import { PendingStatus } from '@/util/AppUtil'

const props = defineProps<{
  personId: string
}>()

const dataGroupShareModalRef = ref<InstanceType<typeof DataGroupShareModal>>()
const dataGroupInviteModalRef = ref<InstanceType<typeof DataGroupInviteModal>>()
const removeFamilyGroupModalRef = ref<InstanceType<typeof DataGroupMemberRemoveModal>>()

const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const dataGroupInviteStore = useDataGroupInviteStore()
const userStore = useUserStore()
const personStore = usePersonStore()

const permissionsRef = computed(() => dataGroupStore.getAsyncPermissions(profileGroupIdRef.value, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const dataGroupRef = computed(() => dataGroupStore.getAsyncGroup(profileGroupIdRef.value, LoadMode.EnsureLoaded)?.data)
const familyGroupMembersRef = computed(() => dataGroupMemberStore.getMembersForDataGroups([profileGroupIdRef.value], LoadMode.EnsureLoaded)
  .filter(dm => dm.principalType == PrincipalType.FamilyGroup))
const isDataGroupAdminRef = computed(() => (permissionsRef.value & ItemPermissions.Administrator) == ItemPermissions.Administrator)
const dataGroupInvitesRef = computed(() => !isDataGroupAdminRef.value ? [] :
  dataGroupInviteStore.getInvitesForDataGroup(profileGroupIdRef.value, LoadMode.EnsureLoaded)
    .filter(inv => inv.result == InvitationResult.Pending))
const personRef = computed(() => personStore.getAsyncPerson(props.personId)?.data)

const profileGroupIdRef = computed(() => CompositeId.getGroupId(props.personId)!)
const canEditRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)
const canEditSharingRef = computed(() => (permissionsRef.value & ItemPermissions.ModifyPermissions) > 0)
const ownerRef = computed(() => userStore.getAsyncUser(dataGroupRef.value?.ownerId)?.data) // loaded with group
const isResearchRef = computed(() => dataGroupRef.value?.groupType == DataGroupType.Research)
const profileTypeRef = computed(() => ProfileUtils.getProfileType(personRef.value, dataGroupRef.value, ownerRef.value))
const isSharedOrRequestedRef = computed(() => dataGroupInvitesRef.value.length || familyGroupMembersRef.value.length)

function onSelectDataGroupInvite(inv: DataGroupInvite) {
  dataGroupInviteModalRef.value?.openEdit(inv.id!)
}

function onRequestShare(familyGroupId: string) {
  dataGroupInviteModalRef.value?.openNew(profileGroupIdRef.value, familyGroupId)
}

function onShare() {
  const familyGroupIds = familyGroupMembersRef.value.map(m => m.principalId!)
  familyGroupIds.push(...dataGroupInvitesRef.value.map(inv => inv.principalId!))
  dataGroupShareModalRef.value?.open(profileGroupIdRef.value, familyGroupIds)
}

function onRemoveFamilyGroup(dm: DataGroupMember) {
  removeFamilyGroupModalRef.value?.open(dm)
}

</script>